"use client";

import { useState } from "react";
import Image from "next/image";
import FbIcon from "@/assets/icons/auth/fb.svg";
import GoogleIcon from "@/assets/icons/auth/google.svg";
import AppleIcon from "@/assets/icons/auth/iphone.svg";
import { buildSocialHref } from "@/lib/auth/socialRedirect";
import { RuntimeConfig } from "@/lib/runtimeConfig";
import SpinLoading from "@/components/skeletons/SpinLoading";

const SOCIAL_PROVIDERS = [
  {
    name: "Facebook",
    icon: FbIcon,
    path: "/v1/auth/facebook",
  },
  {
    name: "Google",
    icon: GoogleIcon,
    path: "/v1/auth/google",
  },
  {
    name: "Apple",
    icon: AppleIcon,
    path: "/v1/auth/apple",
  },
] as const;

const getUserSvcApiUrl = () => {
  if (typeof window !== "undefined") {
    const runtime = (
      window as Window & { __RUNTIME_CONFIG__?: Record<string, string> }
    ).__RUNTIME_CONFIG__;
    if (runtime?.USERSVC_API_URL) {
      return runtime.USERSVC_API_URL;
    }
  }

  return (
    RuntimeConfig?.USERSVC_API_URL ||
    process.env.NEXT_PUBLIC_USERSVC_API_URL ||
    "https://dev.tovtrip.com/usersvc/api"
  );
};

const SocialAuthButtons = () => {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const handleSocialAuthClick = (path: string, providerName: string) => {
    if (loadingProvider) return;

    const baseUrl = getUserSvcApiUrl();
    if (!baseUrl) {
      console.error("[SocialAuth] User service API URL is not available");
      return;
    }

    const cleanBase = baseUrl.replace(/\/+$/, "");
    const fullUrl = `${cleanBase}${path}`;
    const href = buildSocialHref(fullUrl);

    if (!href || href === "#") {
      console.error("[SocialAuth] Invalid redirect URL generated for:", providerName);
      return;
    }

    setLoadingProvider(providerName);
    window.location.assign(href);
  };

  return (
    <div className="flex justify-center gap-4 pt-1 sm:gap-5 sm:pt-2">
      {SOCIAL_PROVIDERS.map((provider) => {
        const isLoading = loadingProvider === provider.name;

        return (
          <button
            key={provider.name}
            type="button"
            disabled={Boolean(loadingProvider)}
            onClick={() => handleSocialAuthClick(provider.path, provider.name)}
            className="flex h-11 w-11 items-center justify-center rounded-full border bg-white transition-colors hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-70 sm:h-12 sm:w-12"
            aria-label={`Continue with ${provider.name}`}
          >
            {isLoading ? (
              <SpinLoading />
            ) : (
              <Image
                src={provider.icon}
                alt={provider.name}
                width={23}
                height={23}
                className="sm:h-[25px] sm:w-[25px]"
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SocialAuthButtons;
